/**
 * Arrastre táctil para el carrusel de testimonios de Judge.me.
 *
 * POR QUÉ EXISTE: el widget apila las tarjetas con `position: absolute` una encima de
 * otra y no tiene pista desplazable — medido: `scrollWidth === clientWidth` en toda la
 * cadena. O sea que no hay nada que arrastrar, y por eso en el celular el carrusel sólo
 * se podía pasar con las flechas. Este script no reimplementa el carrusel: traduce el
 * gesto a un clic en la flecha que corresponde y deja que la app haga su transición.
 *
 * Sólo táctil: en escritorio las flechas están a la vista (ver base.css) y un arrastre
 * con el mouse ahí no aporta nada.
 *
 * El `touch-action: pan-y` que hace falta para que el navegador nos ceda el gesto
 * horizontal vive en base.css, junto al resto del reskin del widget.
 */
(() => {
  const UMBRAL = 45; // px de recorrido horizontal antes de considerarlo un arrastre


  /** @returns {boolean} si el widget quedó cableado (o ya lo estaba) */
  const iniciar = (widget) => {
    if (widget.dataset.swipeListo === 'true') return true;
    const pista = widget.querySelector('.jdgm-content');
    const flechas = widget.querySelectorAll('.jdgm-arrow');
    // Sin las dos flechas no hay a quién delegarle el cambio de tarjeta.
    //
    // Devolver `false` acá —y no `true`— es lo que sostiene al observador de abajo: la app pinta
    // el contenedor del carrusel ANTES que sus flechas, así que hay un intervalo en el que el
    // widget ya existe en el DOM pero todavía no se puede cablear. Tratar "existe" como "listo"
    // apagaría el observador en ese hueco y el arrastre no se activaría nunca.
    if (!pista || flechas.length < 2) return false;

    widget.dataset.swipeListo = 'true';
    // Por orden en el DOM, no por aria-label: la app traduce esas etiquetas según el
    // idioma configurado y el selector se rompería al cambiarlo.
    const [anterior, siguiente] = flechas;

    let x0 = null;
    let y0 = null;
    let arrastro = false;

    pista.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType === 'mouse') return;
        x0 = e.clientX;
        y0 = e.clientY;
        arrastro = false;
      },
      { passive: true }
    );

    pista.addEventListener(
      'pointermove',
      (e) => {
        if (x0 === null || arrastro) return;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        // Más horizontal que vertical, o le robamos el scroll de la página al cliente.
        if (Math.abs(dx) > UMBRAL && Math.abs(dx) > Math.abs(dy)) arrastro = true;
      },
      { passive: true }
    );

    const soltar = (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0;
      x0 = null;
      if (!arrastro) return;
      (dx < 0 ? siguiente : anterior).click();
      // `arrastro` queda en true a propósito hasta el clic de abajo: es la señal para
      // suprimirlo. Se limpia en el próximo pointerdown si ese clic nunca llega.
    };

    pista.addEventListener('pointerup', soltar, { passive: true });
    pista.addEventListener('pointercancel', () => { x0 = null; }, { passive: true });

    // Un arrastre que termina sobre el enlace al producto dispara igual su clic y te
    // saca de la página. Se suprime sólo cuando hubo arrastre: un toque normal navega.
    // Nunca se usa setPointerCapture para esto — la captura redirige la secuencia y
    // mata TODOS los clics de adentro, no sólo el del arrastre.
    pista.addEventListener(
      'click',
      (e) => {
        if (!arrastro) return;
        arrastro = false;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
    return true;
  };

  /** @returns {boolean} si quedó al menos un carrusel efectivamente cableado */
  const barrer = () => {
    const widgets = [...document.querySelectorAll('.jdgm-testimonials-carousel')];
    // `map` y no `some`: `some` corta en el primer `true` y dejaría sin cablear a los carruseles
    // que vinieran después. Se cablean todos, y recién ahí se decide si hubo alguno.
    return widgets.map(iniciar).some(Boolean);
  };

  if (barrer()) return;

  // El widget lo pinta la app después del load. Un observador lo engancha cuando aparezca.
  //
  // ESTE OBSERVADOR ERA EL PEOR COSTO DE RUNTIME DEL TEMA, y por tres razones que se suman:
  // se instalaba en TODAS las páginas (el widget vive sólo en la portada), no se desconectaba
  // NUNCA, y su callback hace un `querySelectorAll` de documento entero. O sea: cada mutación
  // del DOM —cada morph del carrito, cada apertura de cajón, cada clon que agrega la cinta de
  // anuncios en su bucle— disparaba un barrido completo del documento que en el 100% de las
  // páginas que no son la portada no podía encontrar nada.
  //
  // El `@doc` de `snippets/testimonials-swipe.liquid` dice que "el script sale por la puerta en
  // cuanto no encuentra el carrusel". Esa era la intención; el código no la cumplía. Ahora sí:
  // sin widget presente se sale arriba, y si hay que esperarlo el observador se apaga solo.
  //
  // Tres guardas, copiadas de `assets/reviews-badge-scroll.js`, que resuelve exactamente este
  // problema en este mismo repo:
  //   1. se desconecta apenas engancha algo,
  //   2. corta por tiempo pase lo que pase,
  //   3. coalesce las ráfagas en un solo barrido por frame — la app pinta el widget nodo por
  //      nodo, así que sin esto una sola aparición cuesta decenas de barridos.
  if (!document.body) return;

  let agendado = false;
  const obs = new MutationObserver(() => {
    if (agendado) return;
    agendado = true;
    requestAnimationFrame(() => {
      agendado = false;
      if (barrer()) obs.disconnect();
    });
  });
  obs.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => obs.disconnect(), 15000);
})();
